import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { withStyles, darken } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import MuiIconButton from '@material-ui/core/IconButton'
import { green, red, orange, blue } from '@material-ui/core/colors'
import { defaultColor } from './common'
import { useTranslation } from 'react-i18next'

const defaultMuiButtonSize = 'medium'

const defaultHeight = '36px'
const defaultLRPadding = '16px'
const defaultTopPadding = '8px'
const defaultTopPaddingSm = '8px'
const defaultFontSize = '13px'
const defaultHeightSm = '24px'
const defaultLRPaddingSm = '12px'
const defaultFontSizeSm = '11px'
const outlineBorderWidth = '1px'
const defaultDisabledColor = '#C6C6C6'
const disabledButtonFontColor = '#FFFFFF'
const defaultLetterSpacingSm = '1px'
const hoverColor = '#F74406'

export const defaultButtonTextTransform = 'none'
export const defaultBorderRadius = '6px'
export const defaultBorderRadiusSm = '4px'

const defaultFontFamily = undefined

const CoreMUIButton = props => {
  const { backgroundColor, color, hoverBackgroundColor, hoverFontColor, ...restProps } = props
  return <Button {...restProps} />
}

CoreMUIButton.propTypes = {
  backgroundColor: PropTypes.string,
  color: PropTypes.string,
  hoverBackgroundColor: PropTypes.string,
  hoverFontColor: PropTypes.string
}

const ColorButtonBase = withStyles(theme => ({
  root: {
    borderRadius: props => props.borderRadius || defaultBorderRadius,
    textTransform: props => props.textTransform || defaultButtonTextTransform,
    color: props =>
      props.color ? props.color : theme.palette.getContrastText(props.backgroundColor),
    backgroundColor: props => props.backgroundColor,
    letterSpacing: props => props.letterSpacing,
    '&:hover': {
      backgroundColor: props => props.hoverBackgroundColor,
      color: props => props.hoverFontColor,
    },
    '&.Mui-disabled': {
      backgroundColor: props => props.disabledbgcolor || defaultDisabledColor,
      color: props => props.color || disabledButtonFontColor,
    },
  },
}))(CoreMUIButton)

const baseButtonStylesSm = {
  height: defaultHeightSm,
  fontSize: defaultFontSizeSm,
  fontFamily: defaultFontFamily,
  lineHeight: '',
  letterSpacing: defaultLetterSpacingSm,
  padding: '0px',
  paddingTop: defaultTopPaddingSm,
  paddingRight: defaultLRPaddingSm,
  paddingLeft: defaultLRPaddingSm,
  borderRadius: defaultBorderRadiusSm
}

const baseButtonStyles = {
  height: defaultHeight,
  fontSize: defaultFontSize,
  fontFamily: defaultFontFamily,
  paddingTop: defaultTopPadding,
  paddingBottom: defaultTopPadding,
  paddingRight: defaultLRPadding,
  paddingLeft: defaultLRPadding,
  borderRadius: defaultBorderRadius,
}

export const BaseButton = props => {
  const {
    sm,
    textOnly,
    outlined,
    textColor,
    baseColor,
    hoverBackgroundColor,
    noTranslate,
    ...finalProps
  } = props
  const { t } = useTranslation()
  let buttonStyles = { ...baseButtonStyles }
  const additionalProps = {}
  if (textOnly) {
    additionalProps.color = baseColor
    additionalProps.hoverFontColor = hoverColor
    additionalProps.hoverBackgroundColor = 'transparent'
    additionalProps.backgroundColor = 'transparent'
    buttonStyles.fontFamily = defaultFontFamily
    buttonStyles.fontWeight = 600
    if (props.disabled) {
      additionalProps.color = defaultDisabledColor
      buttonStyles.fontFamily = defaultFontFamily
      buttonStyles.fontWeight = 400
    }
  } else if (outlined) {
    additionalProps.color = baseColor
    additionalProps.hoverBackgroundColor = 'transparent'
    additionalProps.backgroundColor = 'transparent'
    buttonStyles.border = `${outlineBorderWidth} solid ${baseColor}`
    buttonStyles.fontFamily = defaultFontFamily
    buttonStyles.fontWeight = 600
    if (props.disabled) {
      additionalProps.color = defaultDisabledColor
      buttonStyles.border = `${outlineBorderWidth} solid ${defaultDisabledColor}`
      buttonStyles.fontFamily = defaultFontFamily
      additionalProps.disabledbgcolor = 'transparent'
      buttonStyles.fontWeight = 400
    }
  } else {
    additionalProps.color = textColor
    additionalProps.hoverBackgroundColor =
      hoverBackgroundColor !== undefined ? hoverBackgroundColor : darken(baseColor, 0.2)
    additionalProps.backgroundColor = baseColor
    if (props.disabled) {
      additionalProps.backgroundColor = defaultDisabledColor
    }
  }
  if (sm) {
    buttonStyles = { ...buttonStyles, ...baseButtonStylesSm }
  }
  const finalStyles = { ...buttonStyles, ...props.style }
  return (
    <ColorButtonBase {...finalProps} {...additionalProps} style={finalStyles}>
      {typeof props.children !== 'string' ? props.children : t(props.children)}
    </ColorButtonBase>
  )
}

BaseButton.propTypes = {
  // The color of the text only, used only if textOnly === false and outlined === false.
  // If not supplied, defaults to theme.palette.getContrastText(backgroundColor)
  textColor: PropTypes.string,
  // The color of the text if textOnly, the text and outline if outlined and background otherwise.
  baseColor: PropTypes.string.isRequired,
  // The color of the background of the button when hovered.
  hoverBackgroundColor: PropTypes.string,
  // Make the button small.
  sm: PropTypes.bool,
  // Outlined style button.
  outlined: PropTypes.bool,
  // Text only style button.
  textOnly: PropTypes.bool,
  // Disable the button.
  disabled: PropTypes.bool,
  // General style override. Is applied after all styles so it takes priority.
  style: PropTypes.object,
  noTranslate: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
}

const ThemeButtonBase = forwardRef((props, ref) => {
  const { backgroundColor, hoverBackgroundColor, textColor, ...finalProps } = props

  return <Button {...finalProps} />
})

ThemeButtonBase.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Prevents the language translation of the children prop when it is a string.
   */
  noTranslate: PropTypes.bool,
  /**
   Component/string to display in the button.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string
  ]).isRequired,
  /**
   Function that is executed when the button is clicked.
   */
  onClick: PropTypes.func,
  /**
   Determines the button style.
   */
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  /**
   Determines the button's color.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'error', 'info', 'warning', 'default']),
  /**
   Makes the button full width within the parent container.
   */
  fullWidth: PropTypes.bool,
  /**
   Determines the button size.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   Determines if the button is disabled.
   */
  disabled: PropTypes.bool,
  /**
  Icon element placed before the children.
   */
  startIcon: PropTypes.node,
  /**
  Icon element placed after the children.
   */
  endIcon: PropTypes.node,
  /**
  Override for the button's background color.
   */
  backgroundColor: PropTypes.string,
  /**
  Override for the button's background hover color. Only valid if backgroundColor is set.
   */
  hoverBackgroundColor: PropTypes.string,
  /**
  Override for the button's text color. Only valid if backgroundColor is set.
   */
  textColor: PropTypes.string,
}


export const ColorButton = withStyles((theme) => ({
  root: {
    color: props => props.textColor || theme.palette.getContrastText(props.backgroundColor),
    backgroundColor: props => props.backgroundColor,
    '&:hover': {
      backgroundColor: props => props.hoverBackgroundColor || props.backgroundColor,
    },
    borderRadius: props => props.size === 'small' ? defaultBorderRadiusSm : defaultBorderRadius,
    textTransform: defaultButtonTextTransform,
    fontWeight: props => props.variant === 'outlined' || !props.variant ? 500 : 500,
    '&:disabled': {
      backgroundColor: theme.palette.buttonDisabledBackgroundColor,
      color: theme.palette.buttonDisabledColor,
    }
  },
  containedSizeSmall: {
    lineHeight: 1.0,
    padding: '8px 10px 7px 10px'
  },
}))(ThemeButtonBase)

const BaseThemedButton = withStyles(theme => ({
  root: {
    borderRadius: props => props.size === 'small' ? defaultBorderRadiusSm : defaultBorderRadius,
    textTransform: defaultButtonTextTransform,
    fontWeight: props => props.variant === 'outlined' || !props.variant ? 500 : 500,
    '&:disabled': {
      backgroundColor: theme.palette.buttonDisabledBackgroundColor,
      color: theme.palette.buttonDisabledColor,
    }
  },
  containedSizeSmall: {
    lineHeight: 1.0,
    padding: '8px 10px 7px 10px'
  },
}))(Button)

BaseThemedButton.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Component/string to display in the button.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string
  ]).isRequired,
  /**
   Function that is executed when the button is clicked.
   */
  onClick: PropTypes.func,
  /**
   Determines the button style.
   */
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  /**
   Determines the button's color.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'default']),
  /**
   Makes the button full width within the parent container.
   */
  fullWidth: PropTypes.bool,
  /**
   Determines the button size.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   Determines if the button is disabled.
   */
  disabled: PropTypes.bool,
  /**
  Icon element placed before the children.
   */
  startIcon: PropTypes.node,
  /**
  Icon element placed after the children.
   */
  endIcon: PropTypes.node,
  /**
  Override for the button's background color.
   */
  backgroundColor: PropTypes.string,
  /**
  Override for the button's background hover color. Only valid if backgroundColor is set.
   */
  hoverBackgroundColor: PropTypes.string,
  /**
  Override for the button's text color. Only valid if backgroundColor is set.
   */
  textColor: PropTypes.string,
}

/**
 * ThemeButton is a basic user interaction component that executes a function based on a user click.
 *
 * @param {object} props component props
 * @returns {ThemeButton} ThemeButton component
 */
export const ThemeButton = forwardRef((props, ref) => {
  const { t } = useTranslation()
  let { noTranslate, backgroundColor, hoverBackgroundColor, textColor, ...finalProps } = props
  if (finalProps.color === 'success') {
    backgroundColor = green[500]
    hoverBackgroundColor = green[700]
    textColor = 'white'
    delete finalProps.color
  } else if (finalProps.color === 'error') {
    backgroundColor = red[600]
    hoverBackgroundColor = red[800]
    textColor = 'white'
    delete finalProps.color
  } else if (finalProps.color === 'warning') {
    backgroundColor = orange[500]
    hoverBackgroundColor = orange[700]
    textColor = 'white'
    delete finalProps.color
  } else if (finalProps.color === 'info') {
    backgroundColor = blue[500]
    hoverBackgroundColor = blue[700]
    textColor = 'white'
    delete finalProps.color
  }

  if (backgroundColor) {
    return (
      <ColorButton
        {...finalProps}
        backgroundColor={backgroundColor}
        hoverBackgroundColor={hoverBackgroundColor}
        textColor={textColor}
        ref={ref}
      >
        {typeof props.children !== 'string' ? props.children : t(props.children)}
      </ColorButton>
    )
  }
  return (
    <BaseThemedButton
      {...finalProps}
      ref={ref}
    >
      {typeof props.children !== 'string' ? props.children : t(props.children)}
    </BaseThemedButton>
  )
})

ThemeButton.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Prevents the language translation of the children prop when it is a string.
   */
  noTranslate: PropTypes.bool,
  /**
   Component/string to display in the button.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string
  ]).isRequired,
  /**
   Function that is executed when the button is clicked.
   */
  onClick: PropTypes.func,
  /**
   Determines the button style.
   */
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  /**
   Determines the button's color.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'error', 'info', 'warning', 'default']),
  /**
   Makes the button full width within the parent container.
   */
  fullWidth: PropTypes.bool,
  /**
   Determines the button size.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   Determines if the button is disabled.
   */
  disabled: PropTypes.bool,
  /**
  Icon element placed before the children.
   */
  startIcon: PropTypes.node,
  /**
  Icon element placed after the children.
   */
  endIcon: PropTypes.node,
  /**
  Override for the button's background color. If this is supplied then the color prop is ignored.
   */
  backgroundColor: PropTypes.string,
  /**
  Override for the button's background hover color. Only valid if backgroundColor is set.
   */
  hoverBackgroundColor: PropTypes.string,
  /**
  Override for the button's text color. Only valid if backgroundColor is set.
   */
  textColor: PropTypes.string,
}


ThemeButton.defaultProps = {
  noTranslate: false,
  variant: 'text',
  color: defaultColor,
  fullWidth: false,
  size: defaultMuiButtonSize
}

/**
 * ContainedButton is a ThemeButton component that defaults to variant='contained' and color='primary'.
 *
 * @param {object} props component props
 * @returns {ContainedButton} ContainedButton component
 */

export const ContainedButton = forwardRef((props, ref) => (
  <ThemeButton
    variant='contained'
    color={defaultColor}
    {...props}
    style={{
      width: 'fit-content'
    }}
    ref={ref}
  />
))

ContainedButton.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Prevents the language translation of the children prop when it is a string.
   */
  noTranslate: PropTypes.bool,
  /**
   Component/string to display in the button.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string
  ]).isRequired,
  /**
   Function that is executed when the button is clicked.
   */
  onClick: PropTypes.func,
  /**
   Makes the button full width within the parent container.
   */
  fullWidth: PropTypes.bool,
  /**
   Determines the button size.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   Determines the button's color.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'error', 'info', 'warning', 'default']),
  /**
   Determines if the button is disabled.
   */
  disabled: PropTypes.bool,
  /**
  Icon element placed before the children.
   */
  startIcon: PropTypes.node,
  /**
  Icon element placed after the children.
   */
  endIcon: PropTypes.node,
  /**
  Override for the button's background color. If this is supplied then the color prop is ignored.
   */
  backgroundColor: PropTypes.string,
  /**
  Override for the button's background hover color. Only valid if backgroundColor is set.
   */
  hoverBackgroundColor: PropTypes.string,
  /**
  Override for the button's text color. Only valid if backgroundColor is set.
   */
  textColor: PropTypes.string,
}

/**
 * OutlinedButton is a ThemeButton component that defaults to variant='outlined' and color='primary'.
 *
 * @param {object} props component props
 * @returns {OutlinedButton} OutlinedButton component
 */
export const OutlinedButton = forwardRef((props, ref) =>
  <ThemeButton
    variant='outlined' 
    color={defaultColor}
    {...props}
    ref={ref}
  />
)

OutlinedButton.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Prevents the language translation of the children prop when it is a string.
   */
  noTranslate: PropTypes.bool,
  /**
   Component/string to display in the button.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string
  ]).isRequired,
  /**
   Function that is executed when the button is clicked.
   */
  onClick: PropTypes.func,
  /**
   Makes the button full width within the parent container.
   */
  fullWidth: PropTypes.bool,
  /**
   Determines the button size.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   Determines the button's color.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'error', 'info', 'warning', 'default']),
  /**
   Determines if the button is disabled.
   */
  disabled: PropTypes.bool,
  /**
  Icon element placed before the children.
   */
  startIcon: PropTypes.node,
  /**
  Icon element placed after the children.
   */
  endIcon: PropTypes.node,
  /**
  Override for the button's background color. If this is supplied then the color prop is ignored.
   */
  backgroundColor: PropTypes.string,
  /**
  Override for the button's background hover color. Only valid if backgroundColor is set.
   */
  hoverBackgroundColor: PropTypes.string,
  /**
  Override for the button's text color. Only valid if backgroundColor is set.
   */
  textColor: PropTypes.string,
}

/**
 * TextButton is a ThemeButton component that defaults to variant='text' and color='primary'.
 *
 * @param {object} props component props
 * @returns {TextButton} TextButton component
 */
export const TextButton = forwardRef((props, ref) =>
  <ThemeButton
    variant='text' 
    color={defaultColor}
    {...props}
    ref={ref}
  />
)

TextButton.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Prevents the language translation of the children prop when it is a string.
   */
  noTranslate: PropTypes.bool,
  /**
   Component/string to display in the button.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string
  ]).isRequired,
  /**
   Function that is executed when the button is clicked.
   */
  onClick: PropTypes.func,
  /**
   Makes the button full width within the parent container.
   */
  fullWidth: PropTypes.bool,
  /**
   Determines the button size.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   Determines the button's color.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'error', 'info', 'warning', 'default']),
  /**
   Determines if the button is disabled.
   */
  disabled: PropTypes.bool,
  /**
  Icon element placed before the children.
   */
  startIcon: PropTypes.node,
  /**
  Icon element placed after the children.
   */
  endIcon: PropTypes.node,
  /**
  Override for the button's background color. If this is supplied then the color prop is ignored.
   */
  backgroundColor: PropTypes.string,
  /**
  Override for the button's background hover color. Only valid if backgroundColor is set.
   */
  hoverBackgroundColor: PropTypes.string,
  /**
  Override for the button's text color. Only valid if backgroundColor is set.
   */
  textColor: PropTypes.string,
}

/**
 * Mui Icon Button.
 *
 * @param {object} props component props
 * @returns {IconButton} IconButton component
 */
export const IconButton = forwardRef((props, ref) =>
  <MuiIconButton
    {...props}
    ref={ref}
  />
)

IconButton.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   The icon to display.
   */
  children: PropTypes.oneOfType([
    PropTypes.node,
  ]),
  /**
   Function that is executed when the button is clicked.
   */
  onClick: PropTypes.func,
  /**
   Determines the button's color.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'default']),
  /**
   Determines the button size.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   Determines if the button is disabled.
   */
  disabled: PropTypes.bool,
}

IconButton.defaultProps = {
  color: 'default',
  size: defaultMuiButtonSize
}
